import { Spot, UserLocation, Region, SortOption } from '../types/spot';

export const GPS_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  timeout: 10_000,
  maximumAge: 0,
};

export const MIN_ACCEPTABLE_GPS_ACCURACY_METERS = 150;

export type LocationFailureKind =
  | 'unsupported'
  | 'insecure_context'
  | 'permission_denied'
  | 'position_unavailable'
  | 'timeout';

export function getGeolocationFailure(error: GeolocationPositionError, language: string = 'en'): {
  kind: LocationFailureKind;
  message: string;
} {
  const isDe = language === 'de';
  const isKr = language === 'kr';

  switch (error.code) {
    case error.PERMISSION_DENIED:
      return {
        kind: 'permission_denied',
        message: isKr
          ? '위치 접근이 거부되었습니다. 브라우저 설정에서 위치 권한을 허용하거나 도시를 수동으로 선택해주세요.'
          : isDe
          ? 'Standortfreigabe wurde verweigert. Erlaube den Standort in den Browser-Einstellungen oder wähle einen Ort manuell.'
          : 'Location access was denied. Please enable location permissions in browser settings or select a city manually.',
      };
    case error.POSITION_UNAVAILABLE:
      return {
        kind: 'position_unavailable',
        message: isKr
          ? '현재 위치를 확인할 수 없습니다. GPS 및 네트워크 상태를 확인 후 다시 시도해주세요.'
          : isDe
          ? 'Der Standort ist momentan nicht verfügbar. Prüfe GPS, WLAN/Mobilfunk und versuche es erneut.'
          : 'Location is currently unavailable. Please check your GPS and network connection and try again.',
      };
    case error.TIMEOUT:
      return {
        kind: 'timeout',
        message: isKr
          ? '위치 확인 시간이 초과되었습니다. 실외로 이동하여 다시 시도해주세요.'
          : isDe
          ? 'Die Standortermittlung hat zu lange gedauert. Gehe möglichst ins Freie und versuche es erneut.'
          : 'Location request timed out. Please step outdoors and try again.',
      };
    default:
      return {
        kind: 'position_unavailable',
        message: isKr
          ? '위치를 확인할 수 없습니다. 다시 시도하거나 도시를 수동으로 선택해주세요.'
          : isDe
          ? 'Der Standort konnte nicht ermittelt werden. Bitte versuche es erneut oder wähle einen Ort manuell.'
          : 'Unable to determine your location. Please try again or select a city manually.',
      };
  }
}

export function isCoordinateInSouthKorea(lat: number, lng: number): boolean {
  // Includes the mainland and the main islands with a small safety margin.
  return lat >= 32.8 && lat <= 38.9 && lng >= 124.3 && lng <= 132.2;
}

/**
 * Automatically detects the Korean province/metropolitan region from GPS coordinates.
 */
export function detectRegionFromCoordinates(lat: number, lng: number): Region | null {
  if (lat === undefined || lng === undefined || isNaN(lat) || isNaN(lng)) return null;

  // 1. Jeju Island (Special Self-Governing Province: 33.0 - 34.0° N, 126.0 - 127.15° E)
  if (lat >= 33.0 && lat <= 34.0 && lng >= 126.0 && lng <= 127.15) {
    return 'Jeju';
  }

  // 2. Busan Metropolitan City
  if (lat >= 35.0 && lat <= 35.38 && lng >= 128.80 && lng <= 129.35) {
    return 'Busan';
  }

  // 3. Incheon Metropolitan City (Mainland, Songdo, Yeongjongdo, Ganghwado)
  if (
    lat >= 37.30 &&
    lat <= 37.85 &&
    lng >= 126.15 &&
    lng <= 126.80 &&
    !(lat >= 37.42 && lat <= 37.71 && lng >= 126.78)
  ) {
    return 'Incheon';
  }

  // 4. Seoul Special City
  if (lat >= 37.42 && lat <= 37.71 && lng >= 126.76 && lng <= 127.19) {
    return 'Seoul';
  }

  // 5. Gyeongju
  if (lat >= 35.65 && lat <= 36.05 && lng >= 129.05 && lng <= 129.50) {
    return 'Gyeongju';
  }

  // 6. Daegu Metropolitan City
  if (lat >= 35.70 && lat <= 36.05 && lng >= 128.35 && lng <= 128.80) {
    return 'Daegu';
  }

  // 7. Jeonju
  if (lat >= 35.75 && lat <= 35.95 && lng >= 127.00 && lng <= 127.25) {
    return 'Jeonju';
  }

  // 8. Gangwon Province
  if (lat >= 37.00 && lat <= 38.65 && lng >= 127.75 && lng <= 129.45) {
    return 'Gangwon';
  }

  // 9. Gyeonggi Province (Capital area outside Seoul & Incheon)
  if (lat >= 36.85 && lat <= 38.35 && lng >= 126.40 && lng <= 127.85) {
    return 'Gyeonggi';
  }

  return null;
}

/**
 * Calculates Great Circle distance between two points using the Haversine formula (in kilometers)
 */
export function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

function toRad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/**
 * Calculates initial compass bearing in degrees (0 = North, 90 = East, 180 = South, 270 = West)
 */
export function calculateBearing(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLon = toRad(lon2 - lon1);
  const y = Math.sin(dLon) * Math.cos(toRad(lat2));
  const x =
    Math.cos(toRad(lat1)) * Math.sin(toRad(lat2)) -
    Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
  let brng = (Math.atan2(y, x) * 180) / Math.PI;
  return (brng + 360) % 360;
}

export function getBearingName(bearing: number): string {
  const directions = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  const index = Math.round(bearing / 45) % 8;
  return directions[index];
}

/**
 * Converts straight-line (Haversine) distance to realistic Korean urban street & transit route distance.
 * Urban circuity factors in Korea (accounting for topography, winding alleys, street grid, rivers):
 * - Walking distance (< 2.0 km): ~1.32x
 * - City roads & bus transit (2.0 - 20.0 km): ~1.28x
 * - Regional highway/metro (20.0 - 80.0 km): ~1.22x
 * - Intercity (> 80.0 km): ~1.15x
 */
export function toRealisticRouteDistance(straightLineKm?: number): number {
  if (straightLineKm === undefined || isNaN(straightLineKm)) return 0;
  if (straightLineKm <= 2.0) return straightLineKm * 1.32;
  if (straightLineKm <= 20.0) return straightLineKm * 1.28;
  if (straightLineKm <= 80.0) return straightLineKm * 1.22;
  return straightLineKm * 1.15;
}

/**
 * Human-friendly realistic route distance format (e.g. "450 m", "2.8 km", "34 km")
 */
export function formatDistance(straightKm?: number, showRoadDistance: boolean = true): string {
  if (straightKm === undefined || isNaN(straightKm)) return '';
  const km = showRoadDistance ? toRealisticRouteDistance(straightKm) : straightKm;
  if (km < 1) {
    const meters = Math.round(km * 1000);
    return `${meters} m`;
  }
  if (km < 10) {
    return `${km.toFixed(1)} km`;
  }
  return `${Math.round(km)} km`;
}

export interface RealisticTransitInfo {
  routeDistanceKm: number;
  straightDistanceKm: number;
  walkingMinutes: number;
  busMinutes: number;
  subwayMinutes: number;
  carMinutes: number;
  primaryMode: 'walk' | 'bus' | 'subway' | 'transit' | 'ktx';
  badgeText: string;
  compactBadgeText: string;
  summaryText: string;
  detailedText: string;
}

/**
 * Estimates realistic Korean urban transit times (Walking, City Bus, Subway, Car/Taxi) based on real Korean transit conditions:
 * - Urban walking in Korea: ~3.6 km/h + crosswalk signal wait (90-180s cycles) + stairs/hills (circuity factor ~1.35x)
 * - City Bus (시내버스/마을버스): ~3.5 min walk to stop + ~7 min bus dispatch wait + ~13.5 km/h in-traffic ride with frequent stops + ~3.5 min walk to destination + transfer penalty for >2km
 * - Subway (지하철): ~5 min walk + ~4 min descending into deep Korean stations (B3-B6) + ~5 min wait + ~33 km/h + ~4 min ascending + ~5 min walk to destination
 * - Car/Taxi: ~16 km/h urban speed limit & traffic signals
 */
export function getRealisticTransitInfo(straightKm?: number, language: string = 'en'): RealisticTransitInfo | null {
  if (straightKm === undefined || isNaN(straightKm)) return null;

  const isDe = language === 'de';
  const isKr = language === 'kr';
  const distStr = formatDistance(straightKm);

  // 1. Realistic Korean street route distance (accounting for superblock grids, hills, alleys)
  let routeDistanceKm: number;
  if (straightKm <= 0.5) {
    routeDistanceKm = straightKm * 1.35;
  } else if (straightKm <= 3.0) {
    routeDistanceKm = straightKm * 1.32;
  } else if (straightKm <= 20.0) {
    routeDistanceKm = straightKm * 1.28;
  } else if (straightKm <= 80.0) {
    routeDistanceKm = straightKm * 1.22;
  } else {
    routeDistanceKm = straightKm * 1.15;
  }

  // 2. Walking: Realistic Korean urban pedestrian speed (~3.6 km/h / 60 m/min)
  // Korean streets have frequent multi-lane crosswalks (90-180s wait), pedestrian footbridges, stairs and steep slopes.
  const crosswalkDelay = straightKm <= 0.3 ? 1 : straightKm <= 1.0 ? 3 : Math.round(3 + (straightKm - 1) * 2.5);
  const walkingMinutes = Math.max(1, Math.round((routeDistanceKm / 3.6) * 60 + crosswalkDelay));

  // 3. City Bus:
  // - Walk to nearest bus stop: ~3.5 min
  // - Wait for bus (Korean dispatch headway): ~7 min average
  // - In-bus transit speed in heavy Korean city traffic: ~13.5 km/h (stopping every 200-300m for boarding/alighting)
  // - Walk from bus stop to destination: ~3.5 min
  // - Transfer buffer: for routes > 2.0 km, transfer or waiting for specific route line adds realistic time
  const transferBuffer = straightKm > 2.0 ? Math.min(15, Math.round((straightKm - 2.0) * 2.0)) : 0;
  const busMinutes = Math.max(
    12,
    Math.round(3.5 + 7 + (routeDistanceKm / 13.5) * 60 + 3.5 + transferBuffer)
  );

  // 4. Subway:
  // - Walk to station: ~5 min
  // - Descend into deep Korean underground station (B3-B6): ~4 min
  // - Platform wait: ~5 min
  // - Subway ride: ~33 km/h
  // - Ascend to exit: ~4 min
  // - Walk to destination: ~5 min
  const subwayMinutes = Math.max(
    20,
    Math.round(5 + 4 + 5 + (routeDistanceKm / 33) * 60 + 4 + 5)
  );

  // 5. Car / Taxi:
  // Korean urban speed limits (50 km/h main / 30 km/h school zones) + long traffic signals: ~16 km/h avg in central Seoul/Incheon
  const carMinutes = Math.max(
    8,
    Math.round((routeDistanceKm / 16) * 60 + 3)
  );

  // Badge Text generation: Prioritizes Bus and Walking minutes
  let primaryMode: RealisticTransitInfo['primaryMode'] = 'walk';
  let badgeText = '';
  let compactBadgeText = '';
  let summaryText = '';
  let detailedText = '';

  // Duration formatting helper
  const formatDuration = (mins: number) => {
    if (mins < 60) {
      return isKr ? `약 ${mins}분` : isDe ? `~${mins} Min` : `~${mins} min`;
    }
    const hours = Math.floor(mins / 60);
    const rem = mins % 60;
    if (rem < 5) {
      return isKr ? `약 ${hours}시간` : isDe ? `~${hours} Std.` : `~${hours} hrs`;
    }
    return isKr ? `약 ${hours}시간 ${rem}분` : isDe ? `~${hours}h ${rem}m` : `~${hours}h ${rem}m`;
  };

  const formatCompactDuration = (mins: number) => {
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem === 0 ? `${hours}h` : `${hours}h ${rem}m`;
  };

  if (straightKm <= 0.4) {
    // Under 400m: Pure walking is much faster than bus
    primaryMode = 'walk';
    badgeText = isKr
      ? `🚶 약 ${walkingMinutes}분 (${distStr})`
      : isDe
      ? `🚶 ~${walkingMinutes} Min (${distStr})`
      : `🚶 ~${walkingMinutes} min (${distStr})`;
    compactBadgeText = `🚶 ${walkingMinutes}m`;
    summaryText = badgeText;
    detailedText = isKr
      ? `🚶 도보 약 ${walkingMinutes}분 (${distStr}) · 가까운 거리입니다`
      : isDe
      ? `🚶 Zu Fuß ~${walkingMinutes} Min (${distStr}) · Direkte Gehdistanz`
      : `🚶 Walk ~${walkingMinutes} min (${distStr}) · Very close by`;
  } else if (straightKm <= 12.0) {
    // 400m - 12km: Display BOTH Bus and Foot minutes as requested
    primaryMode = 'bus';
    const busStr = formatDuration(busMinutes);
    const walkStr = formatDuration(walkingMinutes);
    badgeText = `🚌 ${busStr} · 🚶 ${walkStr}`;
    compactBadgeText = `🚌 ${formatCompactDuration(busMinutes)} · 🚶 ${formatCompactDuration(walkingMinutes)}`;
    summaryText = badgeText;
    detailedText = isKr
      ? `🚌 버스 ${busStr} · 🚶 도보 ${walkStr} (${distStr}) · 🚗 택시 ${formatDuration(carMinutes)}`
      : isDe
      ? `🚌 Bus/Öffis ${busStr} · 🚶 Zu Fuß ${walkStr} (${distStr}) · 🚗 Taxi ${formatDuration(carMinutes)}`
      : `🚌 Bus/Transit ${busStr} · 🚶 Walk ${walkStr} (${distStr}) · 🚗 Taxi ${formatDuration(carMinutes)}`;
  } else if (straightKm <= 40.0) {
    // 12km - 40km: Public transit (Bus/Subway) is primary, walking is too long
    primaryMode = 'transit';
    const transitMin = Math.min(busMinutes, subwayMinutes);
    const transitStr = formatDuration(transitMin);
    badgeText = isKr
      ? `🚌/🚇 ${transitStr} (${distStr})`
      : isDe
      ? `🚌/🚇 ${transitStr} (${distStr})`
      : `🚌/🚇 ${transitStr} (${distStr})`;
    compactBadgeText = `🚌/🚇 ${formatCompactDuration(transitMin)}`;
    summaryText = badgeText;
    detailedText = isKr
      ? `🚌/🚇 대중교통 ${transitStr} (${distStr}) · 지하철/광역버스 추천`
      : isDe
      ? `🚌/🚇 Öffentliche Verkehrsmittel ${transitStr} (${distStr}) · U-Bahn & Bus`
      : `🚌/🚇 Public Transit ${transitStr} (${distStr}) · Subway & Express Bus`;
  } else {
    // > 40km: KTX / Express bus
    primaryMode = 'ktx';
    const hours = (routeDistanceKm / 65).toFixed(1);
    badgeText = isKr
      ? `🚄/고속버스 약 ${hours}시간 (${distStr})`
      : isDe
      ? `🚄/Expressbus ~${hours} Std. (${distStr})`
      : `🚄/Express Bus ~${hours} hrs (${distStr})`;
    compactBadgeText = `🚄 ${hours}h`;
    summaryText = badgeText;
    detailedText = isKr
      ? `🚄 KTX / 고속버스 약 ${hours}시간 (${distStr})`
      : isDe
      ? `🚄 KTX / Expressbus ca. ${hours} Std. (${distStr})`
      : `🚄 KTX / Express Bus approx. ${hours} hrs (${distStr})`;
  }

  return {
    routeDistanceKm,
    straightDistanceKm: straightKm,
    walkingMinutes,
    busMinutes,
    subwayMinutes,
    carMinutes,
    primaryMode,
    badgeText,
    compactBadgeText,
    summaryText,
    detailedText,
  };
}

/**
 * Returns formatted badge text highlighting Bus & Foot minutes (e.g. "🚌 ~24 Min · 🚶 ~35 Min")
 */
export function formatTransitBadge(straightKm?: number, language: string = 'en'): string {
  const info = getRealisticTransitInfo(straightKm, language);
  return info ? info.badgeText : (straightKm !== undefined ? formatDistance(straightKm) : '');
}

/**
 * Returns compact badge text for tight spaces (e.g. "🚌 24m · 🚶 35m")
 */
export function formatCompactTransitBadge(straightKm?: number, language: string = 'en'): string {
  const info = getRealisticTransitInfo(straightKm, language);
  return info ? info.compactBadgeText : (straightKm !== undefined ? formatDistance(straightKm) : '');
}

/**
 * Estimates walking time or bus/transit time for longer distances
 */
export function estimateTravelTime(straightKm?: number, language: string = 'en'): string {
  const info = getRealisticTransitInfo(straightKm, language);
  return info ? info.summaryText : '';
}

/**
 * Augment spots list with calculated distance and bearing from the user's current location
 */
export function augmentAndSortSpots(
  spots: Spot[],
  userLocation: UserLocation,
  radiusKm: number,
  sortBy: SortOption = 'newest'
): Spot[] {
  const processed = spots.map((spot) => {
    const dist = calculateDistance(userLocation.lat, userLocation.lng, spot.lat, spot.lng);
    const brng = calculateBearing(userLocation.lat, userLocation.lng, spot.lat, spot.lng);
    return {
      ...spot,
      calculatedDistance: dist,
      calculatedBearing: brng,
      calculatedBearingName: getBearingName(brng),
    };
  });

  // Filter by radius if radiusKm is not set to infinity (0 = All Korea)
  const filtered = radiusKm > 0 ? processed.filter((s) => (s.calculatedDistance ?? 99999) <= radiusKm) : processed;

  // Sort
  return filtered.sort((a, b) => {
    if (sortBy === 'newest') {
      const aNew = Boolean(a.isNew);
      const bNew = Boolean(b.isNew);
      if (aNew !== bNew) return aNew ? -1 : 1;
      return (b.addedOrder ?? 0) - (a.addedOrder ?? 0);
    }
    if (sortBy === 'distance') {
      return (a.calculatedDistance ?? 0) - (b.calculatedDistance ?? 0);
    }
    if (sortBy === 'rating') {
      return b.rating - a.rating;
    }
    if (sortBy === 'must_see') {
      // Flagship landmark priority: Place N Seoul Tower prominently next to Gyeongbokgung within Seoul
      if (a.region === 'Seoul' && b.region === 'Seoul') {
        const isTopAnchor = (id: string) => id === 'event-gyeongbokgung-night-tour' || id === 'seoul-gyeongbokgung';
        if (a.id === 'seoul-n-seoul-tower' && b.id !== 'seoul-n-seoul-tower') {
          return isTopAnchor(b.id) ? 1 : -1;
        }
        if (b.id === 'seoul-n-seoul-tower' && a.id !== 'seoul-n-seoul-tower') {
          return isTopAnchor(a.id) ? -1 : 1;
        }
      }
      if (a.isMustSee !== b.isMustSee) return a.isMustSee ? -1 : 1;
      return (a.calculatedDistance ?? 0) - (b.calculatedDistance ?? 0);
    }
    if (sortBy === 'name') {
      return a.name.localeCompare(b.name);
    }
    return 0;
  });
}
